import {Component, EventEmitter, Input, LOCALE_ID, Output, SimpleChanges} from '@angular/core';
import { DatePipe, JsonPipe, NgForOf, NgIf } from "@angular/common";
import { Router, RouterLink } from "@angular/router";
import { ClrDatepickerModule, ClrInputModule, ClrRadioModule, ClrSelectModule, ClrStepperModule } from "@clr/angular";
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { RequestCompanySearchComponent } from "../../requests/request-company-search/request-company-search.component";
import { Companies, Conditions, Jobs, JobSteps, Personen, Staff, WebserviceResponse } from "../../../_shared/interfaces";
import { Subject } from "rxjs";
import {
  AlertService,
  ConditionStoreService,
  IAlertType, JobsStoreService,
  PersonsStoreService,
  StaffStoreService
} from "../../../_shared/services";
import { ClarityIcons, twoWayArrowsIcon } from "@cds/core/icon";

import { environment as env } from "../../../../environments/environment";
import {TranslocoDirective} from "@jsverse/transloco";

import { CountrySelectComponent } from '../../../_shared/components/country-select/country-select.component';
import { sichtbareMitarbeiter } from "../../../_shared/utils/staff-selection";

@Component({
  selector: 'app-job-form',
  standalone: true,
  imports: [
    CountrySelectComponent,
    JsonPipe,
    RouterLink,
    ClrStepperModule,
    ReactiveFormsModule,
    ClrInputModule,
    ClrSelectModule,
    NgIf,
    ClrDatepickerModule,
    ClrRadioModule,
    NgForOf,
    RequestCompanySearchComponent,
    DatePipe,
    TranslocoDirective
  ],
  templateUrl: './job-form.component.html',
  styleUrl: './job-form.component.sass',
  providers: [{ provide: LOCALE_ID, useValue: 'de' }]
})
export class JobFormComponent {
  jobForm!: FormGroup;
  private defaultDate: Date = new Date();
  persons: Personen[] = [];
  /** Ungefilterte Antwort des Servers. Fuer die Anzeige siehe `staff`. */
  staffAll: Staff[] = [];

  /**
   * Nur aktive Mitarbeiter -- und zusaetzlich der aktuell zugewiesene, auch
   * wenn er passiv ist. Siehe `sichtbareMitarbeiter`; ohne diese Ausnahme
   * verloere das Bearbeiten eines Altvorgangs seinen Bearbeiter.
   */
  get staff(): Staff[] {
    return sichtbareMitarbeiter(this.staffAll, this.jobForm);
  }
  delivery_conditions: Conditions[] = [];
  payment_conditions: Conditions[] = [];
  offer_conditions: Conditions[] = [];

  @Input() projectId: string = '';
  @Input() jobId: number = 0;
  @Input() jobsData?: Jobs;
  @Input() isUpdateMode = true;
  @Output() submitJob = new EventEmitter<JobSteps>();

  devMode: boolean = false;
  crmMode: boolean = false;

  private destroy$ = new Subject<void>();

  constructor(
    private jobss: JobsStoreService,
    private personsService: PersonsStoreService,
    private staffService: StaffStoreService,
    private alertDispatcher: AlertService,
    private condss: ConditionStoreService,
    private router: Router,
    private fb: FormBuilder,
    private datePipe: DatePipe
  ) {
    ClarityIcons.addIcons(twoWayArrowsIcon);
    this.devMode = env.stages.dev;
    this.initForm();
  }

  ngOnInit() {
    this.loadStaff();
    this.loadPersons(parseInt(this.splitProjectNumber(this.projectId, 0)));
    this.loadPayConds();
    this.loadDeliveryConds();
    this.loadOfferConds();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['jobsData'] && this.jobsData) {
      this.patchFormValues(this.jobsData);
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initForm() {
    this.jobForm = this.fb.group({
      stepI: this.fb.group({
        project_id: [''],
        job_id: [''],
        company_name: [''],
        department: [''],
        street: [''],
        postcode: [''],
        city: [''],
        country: [''],
        status: [''],
        name_project: new FormControl('', [Validators.required, Validators.maxLength(255)]),
        description: [''],
        request_id: [''],
        order_id:  [''],
        job_date: this.datePipe.transform(this.defaultDate, 'dd.MM.yyyy'),
        delivery_date: this.datePipe.transform(this.defaultDate, 'dd.MM.yyyy'),
        budget: 0
      }),
      stepII: this.fb.group({
        employee_phm: [''],
      }),
      stepIII: this.fb.group({
        employee_leader: [''],
      }),
      stepIV: this.fb.group({
        cond_offer: [''],
        cond_payment: [''],
        cond_delivery: [''],
        delivery_remarks: [''],
      }),
      stepV: this.fb.group({
        delivery_company: [''],
        delivery_department: [''],
        delivery_street: [''],
        delivery_postcode:  [''],
        delivery_city:  [''],
        delivery_country:  [''],
      }),
      stepVI: this.fb.group({
        invoice_company: [''],
        invoice_department: [''],
        invoice_street: [''],
        invoice_postcode: [''],
        invoice_city: [''],
        invoice_country: [''],
        invoice_vat_id: [''],
        invoice_leitweg_id: [''],
        invoice_einvoice_endpoint: [''],
      })
    })
  }

  private patchFormValues(data: Jobs) {
    if (!data) return;
    const employeePhm = this.toStringOrEmpty(data.employee_phm);
    const employeeLeader = this.toStringOrEmpty(data.employee_leader);
    const formValue = {
      stepI: {
        project_id: data.project_id || '',
        job_id: data.job_id || '',
        company_name: data.company_name || '',
        department: data.department || '',
        street: data.street || '',
        postcode: data.postcode || '',
        city: data.city || '',
        country: this.mapCountryForForm(data.country || ''),
        status: data.status || '',
        name_project: data.name_project || '',
        description: data.description || '',
        request_id: data.request_id || '',
        order_id: data.order_id || '',
        job_date: this.datePipe.transform(data.job_date, 'dd.MM.yyyy'),
        delivery_date: this.datePipe.transform(data.delivery_date, 'dd.MM.yyyy'),
        budget: data.budget || 0
      },
      stepII: {
        employee_phm: employeePhm,
      },
      stepIII: {
        employee_leader: employeeLeader,
      },
      stepIV: {
        cond_offer: data.cond_offer || '',
        cond_payment: data.cond_payment || '',
        cond_delivery: data.cond_delivery || '',
        delivery_remarks: data.delivery_remarks || '',
      },
      stepV: {
        delivery_company: data.delivery_company || '',
        delivery_department: data.delivery_department || '',
        delivery_street: data.delivery_street || '',
        delivery_postcode: data.delivery_postcode || '',
        delivery_city: data.delivery_city || '',
        delivery_country: this.mapCountryForForm(data.delivery_country || ''),
      },
      stepVI: {
        invoice_company: data.invoice_company || '',
        invoice_department: data.invoice_department || '',
        invoice_street: data.invoice_street || '',
        invoice_postcode: data.invoice_postcode || '',
        invoice_city: data.invoice_city || '',
        invoice_country: this.mapCountryForForm(data.invoice_country || ''),
        invoice_vat_id: data.invoice_vat_id || '',
        invoice_leitweg_id: data.invoice_leitweg_id || '',
        invoice_einvoice_endpoint: data.invoice_einvoice_endpoint || '',
      }
    };
    this.jobForm.patchValue(formValue);
  }

  preFillDelivery(supplier: Companies) {
    const update = {
      stepV:
        {
          delivery_company: supplier.company,
          delivery_department: supplier.department,
          delivery_street: supplier.street,
          delivery_postcode:  supplier.postcode,
          delivery_city:  supplier.city,
          delivery_country: this.mapCountryForForm(supplier.country),
        }
    }
    this.jobForm.patchValue(update);
  }

  preFillInvoice(supplier: Companies) {
    const update = {
      stepVI:
        {
          invoice_company: supplier.company,
          invoice_department: supplier.department,
          invoice_street: supplier.street,
          invoice_postcode: supplier.postcode,
          invoice_city: supplier.city,
          invoice_country: this.mapCountryForForm(supplier.country),
          invoice_vat_id: supplier.vat_id,
          invoice_leitweg_id: supplier.leitweg_id || '',
          invoice_einvoice_endpoint: supplier.einvoice_endpoint || '',
        }
    }
    this.jobForm.patchValue(update);
  }

  private mapCountryForForm(country?: string | null): string {
    if (!country) return '';
    const trimmed = country.trim();
    if (!trimmed) return '';
    const upper = trimmed.toUpperCase();
    if (upper === 'DE' || trimmed === 'Deutschland' || trimmed === 'Germany') return 'DE';
    if (upper === 'AT' || trimmed === 'Österreich' || trimmed === 'Austria') return 'AT';
    if (upper === 'CH' || trimmed === 'Schweiz' || trimmed === 'Switzerland') return 'CH';
    return trimmed;
  }

  private toNumberOrEmpty(value: number | string | null | undefined): number | '' {
    if (value === null || value === undefined || value === '') return '';
    const parsed = typeof value === 'number' ? value : parseInt(value, 10);
    return Number.isNaN(parsed) ? '' : parsed;
  }

  private toStringOrEmpty(value: number | string | null | undefined): string {
    const parsed = this.toNumberOrEmpty(value);
    return parsed === '' ? '' : parsed.toString();
  }

  updateJob() {
    if (this.jobForm.valid) {
      const formValue = this.jobForm.value;
      this.jobss.updateJob(formValue, this.jobId).subscribe({
        next: () => {
          this.alertDispatcher.show({
            message: 'Job erfolgreich aktualisiert',
            clrAlertType: IAlertType.SUCCESS
          }, 5000);
          this.submitJob.emit(formValue);
          this.router.navigate(['/job/details', this.projectId, this.jobId]);
        },
        error: (error) => {
          this.alertDispatcher.show({
            message: 'Fehler beim Aktualisieren des Jobs: ' + error,
            clrAlertType: IAlertType.DANGER
          }, 5000);
        }
      });
    }
  }

  splitProjectNumber(projectId: string, substring: number) {
    var splits = projectId.split('.');
    return splits[substring];
  }

  projectLeader(): number {
    return this.toNumberOrEmpty(this.jobForm.get('stepII')?.get('employee_phm')?.value) || 0;
  }

  private loadStaff() {
    this.staffService.getStaff().subscribe({
      next: (response: WebserviceResponse<Staff[]>) => {
        if (response?.object) {
          this.staffAll = response.object;
        }
      },
      error: (error: any) => {
        this.alertDispatcher.show({
          message: 'Fehler beim Laden der Mitarbeiter: ' + error,
          clrAlertType: IAlertType.DANGER
        }, 5000);
      }
    });
  }

  private loadPersons(companyId: number){
    this.personsService.getPersonInCompany(companyId, true).subscribe({
      next: (response: WebserviceResponse<Personen[]>) => {
        if (response?.object) {
          this.persons = response.object;
        }
      },
      complete: () => {
        // Sortiere die Liste nach dem Nachnamen
        this.persons.sort((a, b) => {
          if (a.lastname < b.lastname) {
            return -1;
          }
          if (a.lastname > b.lastname) {
            return 1;
          }
          return 0;
        });

      },
      error: (error: any) => {
        this.alertDispatcher.show({
          message: 'Fehler beim Laden der Kunden: ' + error,
          clrAlertType: IAlertType.DANGER
        }, 5000);
        return
      }
    });
  }

  loadOfferConds() {
    this.condss.getConditionByType(3).subscribe({
      next: (data: WebserviceResponse<Conditions[]>) => {
        if (data?.object) {
          this.offer_conditions = data.object;
        }
      },
      complete: () => {},
      error: (error: any) => {
        this.alertDispatcher.show({
          message: 'Fehler beim Laden der Bedingungen: ' + error,
          clrAlertType: IAlertType.DANGER
        }, 5000);
        return
      }
    });
  }

  loadPayConds(){
    this.condss.getConditionByType(2).subscribe({
      next: (data: WebserviceResponse<Conditions[]>) => {
        if (data?.object) {
          this.payment_conditions = data.object;
        }
      },
      complete: () => {},
      error: (error: any) => {
        this.alertDispatcher.show({
          message: 'Fehler beim Laden der Bedingungen: ' + error,
          clrAlertType: IAlertType.DANGER
        }, 5000);
        return
      }
    });
  }

  loadDeliveryConds(){
    this.condss.getConditionByType(1).subscribe({
      next: (data: WebserviceResponse<Conditions[]>) => {
        if (data?.object) {
          this.delivery_conditions = data.object;
        }
      },
      complete: () => {},
      error: (error: any) => {
        this.alertDispatcher.show({
          message: 'Fehler beim Laden der Bedingungen: ' + error,
          clrAlertType: IAlertType.DANGER
        }, 5000);
        return
      }
    });
  }

  get isJobValid(): boolean {
    return !!(this.jobsData &&
      this.jobsData.cond_offer > 0 &&
      this.jobsData.cond_payment > 0 &&
      this.jobsData.cond_delivery > 0);
  }

}
